/* ============================================================
   AGILITE QR RECEIPT AUTO-CROP — ISOLATED asset
   ------------------------------------------------------------
   Document-scanner behaviour for the receipt step, entirely
   in-browser (works identically on iOS Safari + Android Chrome —
   there is NO web-accessible native document scanner on either):
   detect the receipt's quadrilateral in the captured photo,
   perspective-warp it flat (background gone, deskewed), preview
   it, and let the shopper adjust the corners or keep the full
   photo. BEST-EFFORT by design: any failure, timeout or missing
   dependency falls back to the untouched original photo —
   cropping is UX polish, the OCR does not need it.

   Engines: jscanify (vendored, MIT) on top of OpenCV.js 4.7.0
   (single-file build with embedded WASM, hosted OFF-theme and
   lazy-loaded from window.__AGB_OPENCV_URL only when the receipt
   step is reached — ~9 MB raw / ~2.5 MB over the wire, too big
   and minifier-hostile for a theme asset). Single-threaded build
   on purpose: the threaded/SIMD builds need COOP/COEP headers a
   Shopify storefront cannot set.

   Gating: window.__AGB_OPENCV_URL unset/null => enabled() false
   and qr-receipt.js behaves exactly as before this asset existed.

   Public API (consumed by qr-receipt.js):
     AGBQrCrop.enabled()                    -> feature on?
     AGBQrCrop.warm()                       -> prefetch OpenCV (fire-and-forget)
     AGBQrCrop.process(blob, onStatus)      -> Promise<{blob, cropped}>  (never rejects)
     AGBQrCrop.offerControls(opts)          -> injects "Adjust crop"/"Use full photo"
     AGBQrCrop.reset()                      -> drop per-photo state (retake)
   ============================================================ */
(function () {
  'use strict';

  var DETECT_DIM = 1000;   // detection runs downscaled (speed); corners scale back up
  // Working canvas cap = the upload cap. Bigger buys nothing downstream and the
  // extra canvas + WASM Mats were tipping iOS Safari into "low memory" errors.
  var FULL_DIM = 1600;
  var JPEG_Q = 0.9;
  var MIN_AREA_RATIO = 0.10, MAX_AREA_RATIO = 0.97;  // detected quad sanity vs whole image
  var CV_TIMEOUT_MS = 15000;

  // Per-photo session: the full-res canvas + last corners, for the adjust UI.
  var _session = null;   // {canvas, corners|null, cropped}
  var _ui = null;        // adjust-overlay elements while open

  function enabled() {
    return !!(window.__AGB_OPENCV_URL && window.jscanify);
  }

  // ---- OpenCV loader (lazy, once) --------------------------------------
  var _cvLoad = null;
  function loadCv() {
    if (_cvLoad) return _cvLoad;
    _cvLoad = new Promise(function (resolve, reject) {
      if (!window.__AGB_OPENCV_URL) return reject(new Error('cv_disabled'));
      if (typeof window.cv !== 'undefined' && window.cv.Mat) return resolve(window.cv);
      var s = document.createElement('script');
      s.src = window.__AGB_OPENCV_URL; s.async = true;
      var to = window.setTimeout(function () { reject(new Error('cv_timeout')); }, CV_TIMEOUT_MS);
      s.onload = function () {
        // The docs.opencv.org builds init async: either global cv with
        // onRuntimeInitialized, or (newer) cv as a then-able factory.
        var cv = window.cv;
        function done() { window.clearTimeout(to); resolve(window.cv); }
        if (cv && typeof cv.then === 'function') {
          // Emscripten's Module.then is a FAKE thenable: chaining returns
          // undefined, and Promise.resolve() assimilates it forever (the
          // resolved module is itself thenable) — pegging the main thread.
          // Call it directly, once, and strip .then off the ready module.
          var settled = false;
          cv.then(function (mod) {
            if (settled) return;
            settled = true;
            try { delete mod.then; } catch (e) {}
            window.cv = mod;
            done();
          });
        } else if (cv && cv.Mat) {
          done();
        } else if (cv) {
          cv.onRuntimeInitialized = done;
        } else {
          window.clearTimeout(to); reject(new Error('cv_missing'));
        }
      };
      s.onerror = function () { window.clearTimeout(to); reject(new Error('cv_load_failed')); };
      document.head.appendChild(s);
    });
    _cvLoad.catch(function () { /* keep the rejection; enabled() callers fall back */ });
    return _cvLoad;
  }
  function warm() { if (enabled()) loadCv().catch(function () {}); }

  // ---- geometry helpers ------------------------------------------------
  function quadArea(c) {  // shoelace over TL,TR,BR,BL
    var p = [c.topLeftCorner, c.topRightCorner, c.bottomRightCorner, c.bottomLeftCorner];
    var a = 0;
    for (var i = 0; i < 4; i++) {
      var q = p[(i + 1) % 4];
      a += p[i].x * q.y - q.x * p[i].y;
    }
    return Math.abs(a / 2);
  }
  function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

  function validCorners(c, w, h) {
    if (!c || !c.topLeftCorner || !c.topRightCorner || !c.bottomLeftCorner || !c.bottomRightCorner) return false;
    var ratio = quadArea(c) / (w * h);
    if (ratio < MIN_AREA_RATIO || ratio > MAX_AREA_RATIO) return false;
    var minEdge = Math.min(
      dist(c.topLeftCorner, c.topRightCorner), dist(c.bottomLeftCorner, c.bottomRightCorner),
      dist(c.topLeftCorner, c.bottomLeftCorner), dist(c.topRightCorner, c.bottomRightCorner));
    return minEdge > 0.05 * Math.max(w, h);
  }

  function scaleCorners(c, f) {
    function s(p) { return { x: p.x * f, y: p.y * f }; }
    return { topLeftCorner: s(c.topLeftCorner), topRightCorner: s(c.topRightCorner),
             bottomLeftCorner: s(c.bottomLeftCorner), bottomRightCorner: s(c.bottomRightCorner) };
  }

  // Re-label 4 free points as TL/TR/BL/BR by centroid quadrant (after dragging).
  function normalizeCorners(c) {
    var pts = [c.topLeftCorner, c.topRightCorner, c.bottomLeftCorner, c.bottomRightCorner];
    var cx = (pts[0].x + pts[1].x + pts[2].x + pts[3].x) / 4;
    var cy = (pts[0].y + pts[1].y + pts[2].y + pts[3].y) / 4;
    var out = {};
    pts.forEach(function (p) {
      var key = (p.y < cy ? 'top' : 'bottom') + (p.x < cx ? 'Left' : 'Right') + 'Corner';
      if (!out[key]) out[key] = p;
    });
    return (out.topLeftCorner && out.topRightCorner && out.bottomLeftCorner && out.bottomRightCorner)
      ? out : c;  // degenerate drag (two in one quadrant) — keep as labelled
  }

  function warp(corners) {
    // Output size from the quad's own edge lengths (preserves aspect).
    var w = Math.round((dist(corners.topLeftCorner, corners.topRightCorner) +
                        dist(corners.bottomLeftCorner, corners.bottomRightCorner)) / 2);
    var h = Math.round((dist(corners.topLeftCorner, corners.bottomLeftCorner) +
                        dist(corners.topRightCorner, corners.bottomRightCorner)) / 2);
    if (w < 40 || h < 40) return null;
    var scanner = new window.jscanify();
    return scanner.extractPaper(_session.canvas, w, h, corners);
  }

  // ---- document detection (replaces jscanify's findPaperContour) --------
  // jscanify picks the BIGGEST raw Canny contour — on phone frames that is
  // routinely a table edge or shadow, and its fixed thresholds die under
  // auto-exposure. This is the standard scanner pipeline instead:
  // blur -> auto-threshold Canny -> dilate (close gaps) -> external contours
  // -> approxPolyDP -> best CONVEX QUADRILATERAL in a sane area band; if the
  // edge pass finds no quad, an OTSU bright-region pass (receipts are white
  // paper) gets a second try. Returns labelled corners or null.
  function findCorners(canvas) {
    var cv = window.cv;
    if (!cv || !cv.Mat) return null;
    var src = cv.imread(canvas);
    var gray = new cv.Mat(), blur = new cv.Mat(), edges = new cv.Mat();
    var out = null;
    try {
      cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
      cv.GaussianBlur(gray, blur, new cv.Size(5, 5), 0, 0, cv.BORDER_DEFAULT);
      var mean = cv.mean(blur)[0];
      cv.Canny(blur, edges, Math.max(10, 0.66 * mean), Math.min(255, 1.33 * mean));
      var kernel = cv.Mat.ones(3, 3, cv.CV_8U);
      cv.dilate(edges, edges, kernel);
      kernel.delete();
      out = bestQuad(cv, edges, canvas.width, canvas.height);
      if (!out) {
        var bin = new cv.Mat();
        cv.threshold(blur, bin, 0, 255, cv.THRESH_BINARY + cv.THRESH_OTSU);
        out = bestQuad(cv, bin, canvas.width, canvas.height);
        bin.delete();
      }
    } catch (e) { out = null; }
    finally { src.delete(); gray.delete(); blur.delete(); edges.delete(); }
    return out;
  }

  function bestQuad(cv, binary, w, h) {
    var contours = new cv.MatVector(), hier = new cv.Mat();
    var best = null, bestArea = 0;
    try {
      cv.findContours(binary, contours, hier, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
      var frameArea = w * h;
      for (var i = 0; i < contours.size(); i++) {
        var c = contours.get(i);
        var area = cv.contourArea(c);
        if (area < frameArea * 0.06 || area > frameArea * 0.97) { c.delete(); continue; }
        var approx = new cv.Mat();
        cv.approxPolyDP(c, approx, 0.02 * cv.arcLength(c, true), true);
        if (approx.rows === 4 && cv.isContourConvex(approx) && area > bestArea) {
          var pts = [];
          for (var j = 0; j < 4; j++) {
            pts.push({ x: approx.data32S[j * 2], y: approx.data32S[j * 2 + 1] });
          }
          best = pts; bestArea = area;
        }
        approx.delete(); c.delete();
      }
    } finally { contours.delete(); hier.delete(); }
    if (!best) return null;
    var cx = 0, cy = 0;
    for (var k = 0; k < 4; k++) { cx += best[k].x / 4; cy += best[k].y / 4; }
    var o = {};
    for (var n = 0; n < 4; n++) {
      var p = best[n];
      var key = (p.y < cy ? 'top' : 'bottom') + (p.x < cx ? 'Left' : 'Right') + 'Corner';
      if (!o[key]) o[key] = p;
    }
    return (o.topLeftCorner && o.topRightCorner && o.bottomLeftCorner && o.bottomRightCorner)
      ? o : null;
  }

  // Receipts are light, low-saturation paper. If the AUTO-detected crop isn't,
  // the detector latched onto some other object — fall back to the full photo
  // (the corners are kept so "Adjust crop" still starts from them). Manual
  // crops are never second-guessed.
  function looksLikeReceipt(canvas) {
    var s = document.createElement('canvas');
    s.width = 48;
    s.height = Math.max(1, Math.round(48 * canvas.height / canvas.width));
    var ctx = s.getContext('2d');
    ctx.drawImage(canvas, 0, 0, s.width, s.height);
    var d = ctx.getImageData(0, 0, s.width, s.height).data;
    var lum = 0, sat = 0, n = d.length / 4;
    for (var i = 0; i < d.length; i += 4) {
      var r = d[i], g = d[i + 1], b = d[i + 2];
      lum += 0.299 * r + 0.587 * g + 0.114 * b;
      var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      sat += mx ? (mx - mn) * 255 / mx : 0;
    }
    return (lum / n) > 95 && (sat / n) < 70;
  }

  function toBlob(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (b) { b ? resolve(b) : reject(new Error('encode_failed')); },
                    'image/jpeg', JPEG_Q);
    });
  }

  function decodeToCanvas(blob) {
    var decode = window.createImageBitmap
      ? window.createImageBitmap(blob, { imageOrientation: 'from-image' })
          .catch(function () { return window.createImageBitmap(blob); })
      : Promise.reject(new Error('no_bitmap'));
    return decode.catch(function () {
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(blob);
        var img = new Image();
        img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode_failed')); };
        img.src = url;
      });
    }).then(function (src) {
      var w = src.width || src.naturalWidth, h = src.height || src.naturalHeight;
      var f = Math.min(1, FULL_DIM / Math.max(w, h));
      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * f));
      canvas.height = Math.max(1, Math.round(h * f));
      canvas.getContext('2d').drawImage(src, 0, 0, canvas.width, canvas.height);
      if (src.close) { try { src.close(); } catch (e) {} }
      return canvas;
    });
  }

  // ---- main pipeline ---------------------------------------------------
  // Memory discipline (iOS Safari is the constraint): the photo is decoded
  // ONCE into the capped session canvas — every fallback path re-encodes that
  // small canvas instead of re-decoding the multi-megapixel original, and
  // OpenCV downloads in parallel with the decode (never before the photo
  // exists — preloading it while the camera was open caused OOM kills).
  function fallback(blob) {
    if (_session && _session.canvas) {
      return toBlob(_session.canvas).then(
        function (b) { return { blob: b, cropped: false }; },
        function () { return { blob: blob, cropped: false }; });
    }
    return Promise.resolve({ blob: blob, cropped: false });
  }

  function process(blob, onStatus) {
    _session = null;
    if (!enabled()) return Promise.resolve({ blob: blob, cropped: false });
    var say = typeof onStatus === 'function' ? onStatus : function () {};
    if (!(typeof window.cv !== 'undefined' && window.cv.Mat)) say('loading-cv');
    var decoded = decodeToCanvas(blob).then(function (full) {
      _session = { canvas: full, corners: null, cropped: false };
      return full;
    });
    return Promise.all([loadCv(), decoded]).then(function (ready) {
      var cv = ready[0], full = ready[1];
      say('detecting');
      // Detect downscaled, then scale the corners back to full-res.
      var f = Math.min(1, DETECT_DIM / Math.max(full.width, full.height));
      var det = document.createElement('canvas');
      det.width = Math.max(1, Math.round(full.width * f));
      det.height = Math.max(1, Math.round(full.height * f));
      det.getContext('2d').drawImage(full, 0, 0, det.width, det.height);

      var corners = null;
      var c = findCorners(det);
      if (c && validCorners(c, det.width, det.height)) corners = scaleCorners(c, 1 / f);
      det.width = det.height = 0;             // release the detect buffer now

      if (!corners) return fallback(blob);
      var out = warp(corners);
      if (!out) return fallback(blob);
      _session.corners = corners;             // adjust UI starts here either way
      if (!looksLikeReceipt(out)) return fallback(blob);
      _session.cropped = true;
      return toBlob(out).then(function (b) { return { blob: b, cropped: true }; });
    }).catch(function () { return fallback(blob); });
  }

  // ---- preview controls (injected under the receipt preview) -----------
  // opts: {container, onBlob(blob, label)} — container gets the buttons;
  // onBlob hands the re-cropped/full image back to qr-receipt.js.
  function offerControls(opts) {
    if (!_session || !opts || !opts.container) return;
    var host = opts.container;
    var old = host.querySelector('.agc-controls');
    if (old) old.remove();
    var wrap = document.createElement('div');
    wrap.className = 'agc-controls';
    var adjust = button('Adjust crop', 'ag-btn ag-btn--secondary agc-btn');
    wrap.appendChild(adjust);
    adjust.addEventListener('click', function () { openAdjust(opts); });
    if (_session.cropped) {
      var full = button('Use full photo', 'ag-btn ag-btn--secondary agc-btn');
      wrap.appendChild(full);
      full.addEventListener('click', function () {
        _session.cropped = false;
        toBlob(_session.canvas).then(function (b) { opts.onBlob(b, 'full'); offerControls(opts); });
      });
    }
    host.appendChild(wrap);
  }

  function button(text, cls) {
    var b = document.createElement('button');
    b.type = 'button'; b.className = cls; b.textContent = text;
    return b;
  }

  // ---- the corner-adjust overlay ---------------------------------------
  function defaultCorners() {
    var w = _session.canvas.width, h = _session.canvas.height, inX = w * 0.08, inY = h * 0.08;
    return { topLeftCorner: { x: inX, y: inY }, topRightCorner: { x: w - inX, y: inY },
             bottomLeftCorner: { x: inX, y: h - inY }, bottomRightCorner: { x: w - inX, y: h - inY } };
  }

  function openAdjust(opts) {
    if (_ui || !_session) return;
    // CAPTURE MODE (opts.capture = {onSubmit(blob), onRetake()}): the crop
    // screen IS the whole decision — exactly two buttons, Submit sends the
    // warped crop straight onward, Retake photo abandons the shot.
    var cap = opts.capture || null;
    var full = _session.canvas;
    var host = document.createElement('div');
    host.className = 'agc-adjust';
    var maxW = Math.min((opts.container.closest('section') || document.body).clientWidth || 420, 420);
    var s = Math.min(1, maxW / full.width);
    var vw = Math.round(full.width * s), vh = Math.round(full.height * s);

    var canvas = document.createElement('canvas');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = vw * dpr; canvas.height = vh * dpr;
    canvas.style.width = vw + 'px'; canvas.style.height = vh + 'px';
    canvas.className = 'agc-canvas';
    host.appendChild(canvas);

    var hint = document.createElement('p');
    hint.className = 'agc-hint';
    hint.textContent = 'Drag the corners onto the receipt.';
    host.appendChild(hint);

    var row = document.createElement('div');
    row.className = 'agc-row';
    var apply = button(cap ? 'Submit' : 'Apply crop', 'ag-btn agc-btn');
    var cancel = button(cap ? 'Retake photo' : 'Cancel', 'ag-btn ag-btn--secondary agc-btn');
    row.appendChild(apply); row.appendChild(cancel);
    host.appendChild(row);

    // Working corners in DISPLAY coords.
    var src = _session.corners || defaultCorners();
    var pts = [src.topLeftCorner, src.topRightCorner, src.bottomRightCorner, src.bottomLeftCorner]
      .map(function (p) { return { x: p.x * s, y: p.y * s }; });

    var ctx = canvas.getContext('2d');
    function draw() {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, vw, vh);
      ctx.drawImage(full, 0, 0, vw, vh);
      ctx.fillStyle = 'rgba(28,28,28,.45)';
      ctx.beginPath();
      ctx.rect(0, 0, vw, vh);
      ctx.moveTo(pts[0].x, pts[0].y);
      for (var i = 3; i >= 0; i--) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.closePath();
      ctx.fill('evenodd');
      ctx.strokeStyle = '#92ae74'; ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (var j = 1; j < 4; j++) ctx.lineTo(pts[j].x, pts[j].y);
      ctx.closePath(); ctx.stroke();
      pts.forEach(function (p) {
        ctx.beginPath(); ctx.arc(p.x, p.y, 9, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff'; ctx.fill();
        ctx.lineWidth = 3; ctx.strokeStyle = '#8f1814'; ctx.stroke();
      });
    }
    draw();

    var drag = -1;
    function pointAt(ev) {
      var r = canvas.getBoundingClientRect();
      return { x: Math.min(Math.max(ev.clientX - r.left, 0), vw),
               y: Math.min(Math.max(ev.clientY - r.top, 0), vh) };
    }
    canvas.addEventListener('pointerdown', function (ev) {
      var p = pointAt(ev);
      var best = -1, bestD = 28;      // 28px grab radius (finger-friendly)
      pts.forEach(function (q, i) { var d = Math.hypot(q.x - p.x, q.y - p.y); if (d < bestD) { bestD = d; best = i; } });
      if (best >= 0) { drag = best; canvas.setPointerCapture(ev.pointerId); ev.preventDefault(); }
    });
    canvas.addEventListener('pointermove', function (ev) {
      if (drag < 0) return;
      pts[drag] = pointAt(ev);
      ev.preventDefault();
      draw();
    });
    canvas.addEventListener('pointerup', function () { drag = -1; });
    canvas.addEventListener('pointercancel', function () { drag = -1; });

    cancel.addEventListener('click', function () {
      closeAdjust();
      if (cap && cap.onRetake) cap.onRetake();
    });
    apply.addEventListener('click', function () {
      var finish = function (b) {
        closeAdjust();
        if (cap) { cap.onSubmit(b); }
        else { opts.onBlob(b, 'cropped'); offerControls(opts); }
      };
      var fullPts = normalizeCorners({
        topLeftCorner: { x: pts[0].x / s, y: pts[0].y / s },
        topRightCorner: { x: pts[1].x / s, y: pts[1].y / s },
        bottomRightCorner: { x: pts[2].x / s, y: pts[2].y / s },
        bottomLeftCorner: { x: pts[3].x / s, y: pts[3].y / s }
      });
      var out = warp(fullPts);
      if (!out) {
        // Degenerate corners: in capture mode still submit — the full photo
        // beats a dead end.
        if (cap) { toBlob(_session.canvas).then(finish).catch(closeAdjust); }
        else { closeAdjust(); }
        return;
      }
      _session.corners = fullPts;
      _session.cropped = true;
      toBlob(out).then(finish).catch(closeAdjust);
    });

    // Present it right above the preview block, hide the preview meanwhile.
    var previewBlock = document.getElementById('rc-preview');
    if (previewBlock) previewBlock.hidden = true;
    (previewBlock && previewBlock.parentNode ? previewBlock.parentNode : document.body)
      .insertBefore(host, previewBlock);
    _ui = { host: host, previewBlock: previewBlock };
  }

  function closeAdjust() {
    if (!_ui) return;
    if (_ui.previewBlock) _ui.previewBlock.hidden = false;
    _ui.host.remove();
    _ui = null;
  }

  function reset() {
    closeAdjust();
    _session = null;
    var stale = document.querySelectorAll('.agc-controls');
    for (var i = 0; i < stale.length; i++) stale[i].remove();
  }

  // ---- styles (self-injected; class-scoped) ----------------------------
  var css =
    '.agc-controls{display:flex;gap:8px;margin:2px 0 12px;}' +
    '.agc-btn{flex:1;padding:10px 12px;font-size:11px;}' +
    '.agc-adjust{margin:0 0 14px;}' +
    '.agc-canvas{display:block;margin:0 auto;border:1px solid #cfcfcf;border-radius:4px;background:#1c1c1c;touch-action:none;max-width:100%;}' +
    '.agc-hint{font-size:12px;color:#5c5c5c;text-align:center;margin:8px 0 10px;}' +
    '.agc-row{display:flex;gap:8px;}';
  var tag = document.createElement('style');
  tag.textContent = css;
  document.head.appendChild(tag);

  // Photo -> crop-screen-with-Submit/Retake entry (qr-receipt.js capture
  // flow). -> true when the overlay opened; false = no session (CV failed),
  // caller falls back to the classic preview.
  function openCapture(o) {
    if (!_session) return false;
    openAdjust({ container: o.container,
                 capture: { onSubmit: o.onSubmit, onRetake: o.onRetake } });
    return !!_ui;
  }

  window.AGBQrCrop = { enabled: enabled, warm: warm, process: process,
                       offerControls: offerControls, reset: reset,
                       openCapture: openCapture,
                       ensureCv: loadCv,      // qr-livecam.js: load cv for the live overlay
                       findCorners: findCorners };  // shared document detector
})();
